import { CampaignV2Response } from '@/services/endpoints/campaigns/types';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import React, { FC } from 'react';
import { EmailCampaignListActionsCell } from './EmailCampaignListActionsCell';
import { CampaignListStatus, STATUS_LABELS, STATUS_STYLES } from '@/features/campaigns/v2/Whatsapp/Campaigns/campaignStatus';

export type { CampaignListStatus } from '@/features/campaigns/v2/Whatsapp/Campaigns/campaignStatus';

export type CampaignListItem = {
  id: string;
  name: string;
  subtitle: string;
  status: CampaignListStatus;
  sent?: number;
  delivered?: number;
  deliveredPercent?: number;
  read?: number;
  readPercent?: number;
};

const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);
const formatMetric = (value?: number) => (value === undefined || value === null ? 'N/A' : formatNumber(value));

const ProgressBar: FC<{ value: number; colorClass: string }> = ({ value, colorClass }) => (
  <div className='h-1.5 w-full overflow-hidden rounded-full bg-gray-100'>
    <div className={`h-full rounded-full ${colorClass}`} style={{ width: `${value}%` }} />
  </div>
);

const columnHelper = createColumnHelper<CampaignListItem>();

export type GetEmailCampaignListColumnsOptions = {
  campaigns: CampaignV2Response[];
  isWorkspaceAdmin: boolean;
};

export const getEmailCampaignListColumns = (t: TFunction, { campaigns, isWorkspaceAdmin }: GetEmailCampaignListColumnsOptions) =>
  [
    columnHelper.display({
      id: 'placeholder',
      header: () => '',
      cell: () => <div className='size-4 rounded border border-secondary-border bg-white' />,
      minSize: 48,
      maxSize: 48,
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'campaign',
      header: () => t('campaign:tableHeaders.campaignName'),
      cell: ({ row }) => (
        <>
          <p className='text-sm font-semibold text-slate-900'>{row.original.name}</p>
          <p className='text-secondary-500 mt-1 text-xs'>{row.original.subtitle}</p>
        </>
      ),
      minSize: 220,
      maxSize: 400,
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'status',
      header: () => t('campaign:tableHeaders.status'),
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${STATUS_STYLES[row.original.status]}`}
        >
          {STATUS_LABELS[row.original.status]}
        </span>
      ),
      minSize: 120,
      maxSize: 140,
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'sent',
      header: () => t('campaign:tableHeaders.sent'),
      cell: ({ row }) => <span className='text-sm font-semibold text-slate-900'>{formatMetric(row.original.sent)}</span>,
      minSize: 80,
      maxSize: 100,
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'delivered',
      header: () => t('campaign:tableHeaders.delivered'),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <>
            <p className='text-sm font-semibold text-slate-900'>{formatMetric(item.delivered)}</p>
            {item.deliveredPercent !== undefined ? (
              <div className='mt-2 flex items-center gap-3'>
                <div className='w-36'>
                  <ProgressBar value={item.deliveredPercent} colorClass='bg-blue-500' />
                </div>
                <span className='text-secondary-600 text-xs font-semibold'>{item.deliveredPercent}%</span>
              </div>
            ) : (
              <p className='text-secondary-600 mt-2 text-xs font-semibold'>N/A</p>
            )}
          </>
        );
      },
      minSize: 200,
      maxSize: 260,
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'read',
      header: () => t('campaign:tableHeaders.read'),
      cell: ({ row }) => {
        const item = row.original;
        return (
          <>
            <p className='text-sm font-semibold text-slate-900'>{formatMetric(item.read)}</p>
            {item.readPercent !== undefined ? (
              <div className='mt-2 flex items-center gap-3'>
                <div className='w-36'>
                  <ProgressBar value={item.readPercent} colorClass='bg-emerald-500' />
                </div>
                <span className='text-secondary-600 text-xs font-semibold'>{item.readPercent}%</span>
              </div>
            ) : (
              <p className='text-secondary-600 mt-2 text-xs font-semibold'>N/A</p>
            )}
          </>
        );
      },
      minSize: 200,
      maxSize: 260,
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'actions',
      header: () => t('campaign:tableHeaders.actions'),
      cell: ({ row }) => {
        const campaign = campaigns.find((c) => c.id === row.original.id);
        if (!campaign) {
          return null;
        }

        return <EmailCampaignListActionsCell campaign={campaign} isWorkspaceAdmin={isWorkspaceAdmin} />;
      },
      minSize: 100,
      maxSize: 120,
      enableSorting: false,
    }),
  ] as ColumnDef<CampaignListItem>[];
